import { format } from "date-fns";
import { Icon, Tooltip, Typography } from "@/components/atoms";
import { useProfile } from "@/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getAssetUrl } from "@/utils";
import { getCountryFlagSrc } from "@/utils/getCountryFlagSrc";

interface VehicleDetailsSummaryProps {
  vehicle: Vehicle;
  showHeader?: boolean;
}

export const VehicleDetailsSummary = ({ vehicle, showHeader = true }: VehicleDetailsSummaryProps) => {
  const { dateFormat } = useProfile();
  const flagSrc = vehicle.country?.countryCode ? getCountryFlagSrc(vehicle.country.countryCode) : undefined;

  return (
    <>
      {showHeader && (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-mid-gray">
            <img
              src={getAssetUrl(vehicle.model.avatar, { height: 56, width: 56, background: "transparent", fit: "contain" })}
              className="h-6 w-6"
              alt={`${vehicle.model.make} ${vehicle.model.model}`}
            />
          </div>
          <div className="flex flex-col">
            <Typography variant="title">
              {vehicle.model.make} {vehicle.model.model}
            </Typography>
            <Typography variant="action" className="text-neutral-dark-gray">
              {vehicle.registrationNumber}
            </Typography>
          </div>
        </div>
      )}

      <div className={showHeader ? "mt-4 grid grid-cols-2 gap-3" : "grid grid-cols-2 gap-3"}>
        <DetailRow label="Year" value={vehicle.year?.toString() ?? "-"} />
        <DetailRow
          label="Color"
          value={
            vehicle.color ? (
              <span className="inline-flex items-center gap-2 capitalize">
                <span
                  className="inline-block h-3 w-3 rounded-full border border-black"
                  style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                />
                {vehicle.color.toLowerCase().replace("_", " ")}
              </span>
            ) : (
              "-"
            )
          }
        />
        <DetailRow label="Engine Type" value={vehicle.model.engine ? vehicle.model.engine.toLowerCase() : "-"} />
        <DetailRow label="Passenger Capacity" value={`${vehicle.capacity.pax} pax`} />
        <DetailRow
          label="State Registered"
          value={
            <span className="inline-flex items-center gap-1">
              {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
              {vehicle.state.abbreviation}
            </span>
          }
        />
        <DetailRow
          label="Registration Expiry"
          value={vehicle.registration.expiryDate ? format(new Date(vehicle.registration.expiryDate), dateFormat) : "-"}
        />
        <DetailRow label="VIN" value={vehicle.vin ?? "-"} />
        <DetailRow
          label={
            <span className="inline-flex items-center gap-1">
              Vehicle Category
              <Tooltip
                interactive
                content={
                  <div className="max-w-[295px] font-medium">
                    RideMinder&apos;s system category used for matching vehicles to network jobs
                  </div>
                }
                placement="top"
              >
                <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-dark-gray" size="sm" />
              </Tooltip>
            </span>
          }
          value={vehicle.vehicleCategory?.name ?? "-"}
        />
      </div>
    </>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <>
    <div className="col-span-1 flex items-center">
      <Typography className="text-neutral-dark-gray">{label}</Typography>
    </div>
    <div className="col-span-1 flex items-center justify-end">
      <Typography className="capitalize text-neutral-black">{value}</Typography>
    </div>
  </>
);
